import {
	isValid,
	parseISO,
	startOfDay as startOfDayFNS,
	endOfDay as endOfDayFNS,
	endOfISOWeek as endOfIsoWeekFNS,
	addDays as addDaysFNS,
	addWeeks as addWeeksFNS,
	addYears as addYearsFNS,
	subDays as subDaysFNS,
	subWeeks as subWeeksFNS,
	subMonths as subMonthsFNS,
	subYears as subYearsFNS,
	format,
} from 'date-fns';
import { floatDifferenceOfDates } from '@atlassian/jira-platform-utils-date-fns';
import { getToday } from '@atlassian/jira-software-deployments-common-table/src/utils';
import { BAD_DEPLOYMENTS_DATA_END_DATE } from '../../constants';
import type { DatePickerProps } from '../../types';

export const DATE_REGEX = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;

// This function gives us more granular control about how invalid dates should be handled
// The additional Enums will be used to show different error messages specific to the text input of the date range picker
export const getDate = (dateString: string): Date | 'EMPTY' | 'INVALID-FORMAT' | 'INVALID-DATE' => {
	if (!dateString.length) {
		return 'EMPTY';
	}
	const refinedDateString = dateString.replace(/\//g, '-');
	if (!DATE_REGEX.test(refinedDateString)) {
		return 'INVALID-FORMAT';
	}
	const isValidDate = isValid(new Date(parseISO(refinedDateString)));

	if (!isValidDate) {
		return 'INVALID-DATE';
	}
	return new Date(parseISO(refinedDateString));
};

export const getDay = (date: Date): number => date.getDate();

export const getMonth = (date: Date): number => date.getMonth() + 1;

export const getYear = (date: Date): number => date.getFullYear();

export const startOfDay = (date: Date): Date => startOfDayFNS(date);

export const endOfDay = (date: Date): Date => endOfDayFNS(date);

export const endOfIsoWeek = (date: Date): Date => endOfIsoWeekFNS(date);

export const formatDate = (date: Date, dateFormat: string): string => format(date, dateFormat);

export const addDays = (date: Date, quantity: number): Date => addDaysFNS(date, quantity);

export const addWeeks = (date: Date, quantity: number): Date => addWeeksFNS(date, quantity);

export const addYears = (date: Date, quantity: number): Date => addYearsFNS(date, quantity);

export const subDays = (date: Date, quantity: number): Date => subDaysFNS(date, quantity);

export const subWeeks = (date: Date, quantity: number): Date => subWeeksFNS(date, quantity);

export const subMonths = (date: Date, quantity: number): Date => subMonthsFNS(date, quantity);

export const subYears = (date: Date, quantity: number): Date => subYearsFNS(date, quantity);

export const roundForwardToExcludeBadDeploymentsData = (date: Date) =>
	date < BAD_DEPLOYMENTS_DATA_END_DATE ? BAD_DEPLOYMENTS_DATA_END_DATE : date;

export const roundBackwardToExcludeFutureDates = (date: Date): Date =>
	date > endOfDay(new Date()) ? new Date() : date;

export const isStartDateAfterEndDate = (startDate: Date, endDate: Date): boolean =>
	startDate > endDate;

export const isDateAfterToday = (date: Date): boolean => date > endOfDay(getToday());

// Data before April 2020 is invalid
export const isDateBeforeApril = (date: Date): boolean => date < BAD_DEPLOYMENTS_DATA_END_DATE;

// startDate and endDate are both inclusive so we add 1 day to the endDate
export const isDateRangeMoreThanYearApart = (startDate: Date, endDate: Date): boolean => {
	const duration = floatDifferenceOfDates(addDays(endDate, 1), startDate, 'years');

	return duration > 1;
};

export const getRoundedDate = (
	date: Date,
	{ disableFuture = false, disablePast = false, minDate, maxDate }: DatePickerProps,
): Date => {
	let roundedDate = date;
	if ((disableFuture && date > getToday()) || (disablePast && date < getToday())) {
		roundedDate = getToday();
	}
	if (minDate && date < minDate) {
		roundedDate = minDate;
	}
	if (maxDate && date > maxDate) {
		roundedDate = maxDate;
	}
	return roundedDate;
};
